import { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { isAxiosError } from 'axios';
import api from '@/lib/api';
import { Firm } from '@/types/settings';
import { applyBrandColor, colorway, COLORWAYS, SENTRA_INDIGO } from '@/utils/theme';

interface ThemeSettingsProps {
  firm: Firm;
  canModify: boolean;
  onFirmUpdate: () => void;
}

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * One brand colour per firm. The sidebar and buttons use it directly; the
 * rest of the palette (surfaces, links, charts, dark mode) is derived from it
 * by themeFromBrandColor, so there is nothing else to pick.
 */
export function ThemeSettings({ firm, canModify, onFirmUpdate }: ThemeSettingsProps) {
  const saved = firm.brand_color || SENTRA_INDIGO;
  const [value, setValue] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const valid = HEX.test(value);
  const dirty = valid && value.toLowerCase() !== saved.toLowerCase();

  // Live preview while choosing; put the saved colour back when leaving unsaved
  useEffect(() => {
    if (valid) applyBrandColor(value);
  }, [value, valid]);
  const savedRef = useRef(firm.brand_color);
  useEffect(() => {
    savedRef.current = firm.brand_color;
  }, [firm.brand_color]);
  useEffect(() => () => applyBrandColor(savedRef.current), []);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.patch(`/api/firms/${firm.id}/`, { brand_color: value });
      setMessage({ ok: true, text: 'Brand colour saved.' });
      onFirmUpdate();
    } catch (err) {
      const data = isAxiosError(err) ? err.response?.data : undefined;
      setMessage({
        ok: false,
        text: data?.brand_color?.[0] || data?.detail || 'Could not save the brand colour.',
      });
    } finally {
      setSaving(false);
    }
  };

  const cw = colorway(valid ? value : saved);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Brand colour</CardTitle>
        <CardDescription>
          Your firm's colour is used for the sidebar and buttons; links, charts and dark mode
          are derived from it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {COLORWAYS.map((c) => {
            const selected = value.toLowerCase() === c.hex;
            return (
              <button
                key={c.hex}
                type="button"
                disabled={!canModify}
                onClick={() => setValue(c.hex)}
                aria-pressed={selected}
                className={`rounded-lg p-2 text-left text-sm transition-colors hover:bg-accent disabled:opacity-60 ${
                  selected ? 'ring-2 ring-ring' : ''
                }`}
              >
                <span
                  className="mb-2 block h-10 rounded-md"
                  style={{ background: colorway(c.hex).brand }}
                />
                {c.name}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="brand-color">Custom colour</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Pick a custom colour"
                value={valid ? value : saved}
                disabled={!canModify}
                onChange={(e) => setValue(e.target.value)}
                className="h-9 w-9 cursor-pointer rounded border border-input bg-transparent"
              />
              <Input
                id="brand-color"
                className="w-32 font-mono"
                value={value}
                disabled={!canModify}
                onChange={(e) => setValue(e.target.value.trim())}
                aria-invalid={!valid}
              />
            </div>
          </div>
          {/* Mini preview: sidebar, page, link and button in the derived colours */}
          <div
            className="flex h-16 flex-1 overflow-hidden rounded-lg border"
            style={{ background: cw.canvas }}
            aria-hidden
          >
            <span className="w-24 px-3 py-2 text-xs font-medium text-white" style={{ background: cw.brand }}>
              Sidebar
            </span>
            <span className="flex flex-1 items-center justify-end gap-4 px-4 text-sm">
              <span style={{ color: cw.accent }} className="font-medium">
                Link
              </span>
              <span className="rounded-md px-3 py-1.5 text-white" style={{ background: cw.brand }}>
                Button
              </span>
            </span>
          </div>
        </div>
        {!valid && <p className="text-sm text-destructive">Enter a hex colour like #170098.</p>}

        {message && (
          <Alert variant={message.ok ? 'default' : 'destructive'}>
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {canModify && (
          <div className="flex gap-2">
            <Button onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="outline" onClick={() => setValue(saved)} disabled={!dirty}>
              Reset
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
